'use client'

import { Skeleton } from '@lastbrand/web-ui/skeleton'
import { css } from '@lastbrand/web-ui-styled-system/css'
import React, { useMemo } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { SeoPageRouter } from '@src/components/seo/seo-page-router'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { getUserAgnosticGlobalFeatureGatesFromMemory } from '@src/modules/global-state/user-agnostic-feature-gates.browser'
import { RichText } from '@src/quince-components/src'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import { getSeoMetapageData } from '@src/quince-site-common/src/utils/seo/getSeoMetadata'

import type { CollectionTitleDSProps } from './collection-title-ds.types'

const collectionTitleLoaderSkeletonCss = {
  // eslint-disable-next-line i18next/no-literal-string -- Panda spacing token
  h: 'space-24',
  w: {
    // eslint-disable-next-line i18next/no-literal-string -- Panda width token
    base: 'full',
    // eslint-disable-next-line i18next/no-literal-string -- Panda width token
    lg: 'space-120',
  },
  // eslint-disable-next-line i18next/no-literal-string -- Panda spacing token
  mt: 'space-12',
  mb: {
    // eslint-disable-next-line i18next/no-literal-string -- Panda spacing token
    base: 'space-24',
    lg: '0',
  },
}

export function CollectionTitleDS({
  title,
  hideCollectionPageTitle,
  isRootCollectionPage,
  isJumpLink,
  subCollectionTitle,
  seoData,
  showLoader,
  alternateUrls,
  offerMessage,
}: CollectionTitleDSProps) {
  const isI18nPreviewEnabled = useI18nPreviewContext()
  const { regionDetails, locale } = useLocaleContext()

  const updatedSeoData = useMemo(() => {
    const isAppendQuinceLocale = getUserAgnosticGlobalFeatureGatesFromMemory()['seo-title-append-quince-locale']
    if (subCollectionTitle && seoData) {
      return getSeoMetapageData(
        isI18nPreviewEnabled,
        {
          ...seoData,
          title: subCollectionTitle,
          // TODO: Sujeet - Fix the canonical url for sub collection pages
          canonicalUrl: '', // window.location.href,
        },
        locale,
        regionDetails,
        isAppendQuinceLocale,
        false,
      )
    }
    return getSeoMetapageData(isI18nPreviewEnabled, seoData, locale, regionDetails, isAppendQuinceLocale, false)
  }, [seoData, subCollectionTitle, isI18nPreviewEnabled, locale, regionDetails])

  const wrapperClassName = useMemo(() => css(css.raw({ display: 'flex', flexDirection: 'column' })), [])

  const titleStyles = useMemo(
    () =>
      css.raw(
        {
          display: 'flex',
          alignItems: 'center',
          textTransform: 'uppercase',
          justifyContent: { base: 'center', lg: 'flex-start' },
          // eslint-disable-next-line i18next/no-literal-string -- Panda text style tokens (Figma mobile/desktop title spec)
          textStyle: { base: 'title-14', lg: 'title-20' },
        },
        {
          textAlign: { base: 'center', lg: 'left' },
          mt: { base: '-space-4', lg: '0' },
          p: 0,
          ...(isRootCollectionPage
            ? {
                mt: { base: 'space-10', lg: '0' },
              }
            : {}),
        },
      ),
    [isRootCollectionPage],
  )

  const loaderContainerStyles = useMemo(
    () =>
      css.raw(
        {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          w: 'full',
        },
        { display: 'block' },
      ),
    [],
  )

  const offerMessageStyles = useMemo(
    () =>
      css.raw({
        textStyle: 'body-12',
        fontWeight: 400,
        // eslint-disable-next-line i18next/no-literal-string -- Panda color token
        mt: 'space-4',
        w: { base: 'full', lg: 'auto' },
        maxW: 'full',
        textAlign: { base: 'center', lg: 'left' },
        overflow: { base: 'hidden', lg: 'visible' },
        display: { base: '-webkit-box', lg: 'block' },
        WebkitLineClamp: { base: 2, lg: 'unset' },
        WebkitBoxOrient: { base: 'vertical', lg: 'horizontal' },
        '& p': {
          lg: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        },
      }),
    [],
  )

  const renderCollectionTitle = () => {
    if (hideCollectionPageTitle) return null

    if (showLoader) {
      return (
        <div className={css(loaderContainerStyles)}>
          <Skeleton aria-hidden css={collectionTitleLoaderSkeletonCss} />
        </div>
      )
    }

    return (
      <h1 data-testid={TEST_LOCATORS.COLLECTION_TITLE} className={css(titleStyles)}>
        {isJumpLink ? subCollectionTitle : title}
      </h1>
    )
  }

  const shouldShowOfferMessage = !!offerMessage

  return (
    <div className={wrapperClassName}>
      {subCollectionTitle && (
        <SeoPageRouter
          seoMetaData={updatedSeoData}
          alternateUrls={alternateUrls}
          regionDetails={regionDetails}
          isI18nPreviewEnabled={isI18nPreviewEnabled}
          omitCanonical
        />
      )}
      {renderCollectionTitle()}
      {shouldShowOfferMessage ? (
        <div className={css(offerMessageStyles)}>
          <RichText content={offerMessage} />
        </div>
      ) : null}
    </div>
  )
}
